import React, { useState, useEffect } from 'react';
import { purchaseOrdersAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Package, ArrowRight, RotateCcw, Truck, CheckCircle2, ArrowLeftRight, FileText, Paperclip, Trash2 } from 'lucide-react';

const EVENT_ICONS = {
  po_created: FileText,
  item_received: CheckCircle2,
  item_partially_received: Package,
  transfer_out: ArrowRight,
  transfer_in: ArrowRight,
  restock: Package,
  job_material_used: Truck,
  job_material_reversed: RotateCcw,
  job_allocation: Truck,
  job_return: RotateCcw,
  return_to_inventory: RotateCcw,
  wh_transfer_initiated: ArrowLeftRight,
  wh_transfer_accepted: CheckCircle2,
  document_uploaded: Paperclip,
  document_removed: Trash2,
};

const EVENT_COLORS = {
  po_created: 'text-sky-500 bg-sky-100 dark:bg-sky-900/30',
  item_received: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30',
  item_partially_received: 'text-amber-500 bg-amber-100 dark:bg-amber-900/30',
  transfer_out: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
  transfer_in: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
  job_material_used: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-900/30',
  job_material_reversed: 'text-red-500 bg-red-100 dark:bg-red-900/30',
  return_to_inventory: 'text-teal-500 bg-teal-100 dark:bg-teal-900/30',
  wh_transfer_initiated: 'text-violet-500 bg-violet-100 dark:bg-violet-900/30',
  wh_transfer_accepted: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30',
  document_uploaded: 'text-slate-500 bg-slate-100 dark:bg-slate-800/50',
  document_removed: 'text-red-500 bg-red-100 dark:bg-red-900/30',
};

export function POActivityModal({ poId, poNumber, open, onClose }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !poId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await purchaseOrdersAPI.getActivity(poId);
        setActivities(res.data?.activities || []);
      } catch {
        setActivities([]);
      }
      setLoading(false);
    };
    load();
  }, [poId, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh]" data-testid="po-activity-modal">
        <DialogHeader>
          <DialogTitle className="text-base">Activity Log — PO {poNumber}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">No activity recorded.</div>
        ) : (
          <ScrollArea className="max-h-[55vh] pr-2">
            <div className="relative pl-6 space-y-0">
              {/* Timeline line */}
              <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />
              {activities.map((a, i) => {
                const Icon = EVENT_ICONS[a.type] || FileText;
                const colorClass = EVENT_COLORS[a.type] || 'text-gray-500 bg-gray-100';
                const ts = a.timestamp ? new Date(a.timestamp) : null;
                return (
                  <div key={i} className="relative flex gap-3 py-2.5" data-testid={`activity-row-${i}`}>
                    <div className={`absolute -left-6 w-6 h-6 rounded-full flex items-center justify-center ${colorClass} shrink-0`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium">{a.label}</span>
                        {a.item && <span className="text-xs text-muted-foreground">{a.item}</span>}
                        {a.qty != null && <span className="text-xs font-mono bg-muted px-1 rounded">x{a.qty}</span>}
                      </div>
                      {a.detail && a.detail !== a.label && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{a.detail}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {a.user && <span>{a.user}</span>}
                        {ts && <span> · {ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
